import { Column } from 'primereact/column';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IntBool } from '../../../../../../../enums/booleans';
import { ReconciliationStatus } from '../../../../../../../enums/reconciliations';
import useAxiosHook from '../../../../../../../hooks/useAxiosHook';
import useTableColumns from '../../../../../../../hooks/useTableColumns';
import { UseTableStateReturn } from '../../../../../../../hooks/useTableState';
import useToastMessage from '../../../../../../../hooks/useToastMessage';
import {
  DeliveredShipmentCollectionQueryParams,
  PickedUpShipmentReconciliationCollection,
  ReconciliationResource,
} from '../../../../../../../types/api/reconciliations';
import { WorkOrderResource } from '../../../../../../../types/api/workOrders';
import { queryString } from '../../../../../../../utils/helpers/http';
import Table from '../../../../../../DataTable/Table/Table';
import { additionalRowClassNames } from '../../ShipmentReconciliation.functions';
import { DataRow } from '../../ShipmentReconciliation.types';
import {
  additionalColumnProperties,
  getColumnHeadersMap,
  reconciliationListsCourierDelivered,
} from './PickedUpTable.functions';

type Props = {
  tableInstance: UseTableStateReturn<DataRow>;
  reconciliationData: ReconciliationResource | undefined;
  workOrderData: WorkOrderResource | undefined;
  shouldDisplayData: boolean;
  exportToExcel: () => void;
  setPickedUpShipments: (p: DataRow[]) => void;
  onClickEditDialog: (p: DataRow) => void;
};

function PickedUpTable({
  tableInstance,
  reconciliationData,
  workOrderData,
  shouldDisplayData,
  exportToExcel,
  setPickedUpShipments,
  onClickEditDialog,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [pickedUpShipmentData, setPickedUpShipmentData] =
    useState<PickedUpShipmentReconciliationCollection>();
  const [disabledRows, setDisabledRows] = useState([]);
  const [action, setAction] = useState<string>('');
  const {
    page,
    limit,
    tableRef,
    sortField,
    sortOrder,
    selectionMultiple,
    contextMenuSelection,
    setPage,
    setLimit,
    setSortField,
    setSortOrder,
    setSelectionMultiple,
    setContextMenuSelection,
  } = tableInstance;

  const columnHeadersMap = useMemo(() => getColumnHeadersMap(t), [t]);
  const { columns, columnOptions, selectedColumns, setSelectedColumns } =
    useTableColumns(
      page,
      limit,
      'reconciliationListsCourierDelivered_dataTable',
      columnHeadersMap,
      columnHeadersMap,
      (c) =>
        additionalColumnProperties(
          t,
          c as keyof typeof columnHeadersMap,
          setContextMenuSelection,
          setAction
        )
    );

  const finalColumns = useMemo<JSX.Element[]>(
    () => [
      ...columns,
      <Column
        key="action-column"
        header={t('Actions')}
        field="actions"
        frozen
        alignFrozen="right"
        {...additionalColumnProperties(
          t,
          'actions',
          setContextMenuSelection,
          setAction
        )}
      />,
    ],
    [columns, t, setContextMenuSelection, setAction]
  );

  useEffect(() => {
    if (action && contextMenuSelection) {
      if (action === 'edit') {
        onClickEditDialog(contextMenuSelection);
      }
      setAction('');
    }
  }, [action, contextMenuSelection, onClickEditDialog]);

  const {
    data: _pickedUpShipmentData,
    error: pickedUpShipmentError,
    reload: reloadPickedUpShipmentData,
    isLoading: isPickedUpShipmentDataLoading,
  } = useAxiosHook<PickedUpShipmentReconciliationCollection>(
    '/reconciliations/shipments/picked-up' +
      queryString<DeliveredShipmentCollectionQueryParams>({
        page: 1,
        limit: 0,
        work_order_id:
          reconciliationData?.status_id !== ReconciliationStatus.Approved
            ? reconciliationData?.work_order_id
            : undefined,
        reconciliation_id:
          reconciliationData?.status_id === ReconciliationStatus.Approved
            ? reconciliationData?.id
            : undefined,
      }),
    {
      skipWhen: !workOrderData?.date || !reconciliationData,
    }
  );

  useEffect(() => {
    const disabled = _pickedUpShipmentData?.data.filter(
      (item) => item.is_reconciled === IntBool.True
    );
    setDisabledRows((disabled as any) ?? []);
    setPickedUpShipments(_pickedUpShipmentData?.data ?? []);
    setPickedUpShipmentData(_pickedUpShipmentData);
    setSortField('status_id');
    setSortOrder(1);
  }, [_pickedUpShipmentData, setPickedUpShipments, setSortField, setSortOrder]);

  useToastMessage(undefined, pickedUpShipmentError, {
    error: {
      summary: t('An error occured while reading shipment data'),
    },
  });

  return (
    <Table
      tableRef={tableRef}
      columns={finalColumns}
      data={pickedUpShipmentData}
      isLoading={isPickedUpShipmentDataLoading}
      hasError={!!pickedUpShipmentError}
      reload={reloadPickedUpShipmentData}
      isReloadDisabled={!shouldDisplayData}
      setPage={setPage}
      setLimit={setLimit}
      sortField={sortField}
      rows={limit}
      setSortField={setSortField}
      setSortOrder={setSortOrder}
      setSelection={setSelectionMultiple}
      sortOrder={sortOrder}
      selectionDisabledRows={disabledRows}
      selection={selectionMultiple}
      storageString={reconciliationListsCourierDelivered}
      selectedColumns={selectedColumns}
      setSelectedColumns={setSelectedColumns}
      columnOptions={columnOptions}
      headerTitle=""
      selectionMode="multiple"
      rowClassName={additionalRowClassNames}
      exportToExcelButton
      onExportToExcelButtonClick={
        pickedUpShipmentData?.data.length ? exportToExcel : () => {}
      }
      rebuildTooltip
      scrollHeight="700"
      selectionPageOnly
      minGroupSelection={2}
      contextMenuSelection={contextMenuSelection}
      setContextMenuSelection={setContextMenuSelection}
      paginator={false}
    />
  );
}

export default PickedUpTable;
